import { Box, SpaceBetween } from "@amzn/awsui-components-console";
import React from "react";

import { formatTimestamp } from "@/utils/utils";

/**
 * Rendered in place of an identity the backend has no record of. Admin
 * governance surfaces (Metadata Configuration, Agent Configuration,
 * Export/Transfer Locations) all use this em dash, so it is exported for
 * tests and callers rather than re-typed at each site.
 */
export const NO_IDENTITY_PLACEHOLDER = "—";

export interface LastChangedCellProps {
  /**
   * Who last changed the record, exactly as the API returned it. Rendered
   * unmodified when present. A value that is null, absent from the response,
   * empty, or whitespace-only renders {@link NO_IDENTITY_PLACEHOLDER} instead,
   * so a blank first line is never mistaken for "nobody" and a whitespace
   * string is never mistaken for an identity.
   */
  identity?: string | null;
  /**
   * When the record last changed. Rendered beneath the identity in the
   * de-emphasised secondary style. A falsy value renders no timestamp line at
   * all: `formatTimestamp` would otherwise yield "Invalid Date" for it.
   */
  timestamp?: string | null;
  /**
   * Suffix for the cell's `data-testid`, so a cell can be addressed by the id
   * of the record it belongs to rather than by its position in a list.
   */
  testId?: string;
}

/**
 * Who last changed a record, and when — the identity on the first line with
 * the timestamp de-emphasised beneath it.
 *
 * The rendering deliberately matches the attribution column the Metadata
 * Configuration, Agent Configuration, and Export/Transfer Location admin pages
 * already use, so one attribution style covers every admin governance surface.
 * It lives outside any one feature folder because more than one of those
 * surfaces renders it.
 */
const LastChangedCell: React.FC<LastChangedCellProps> = ({
  identity,
  timestamp,
  testId,
}) => {
  // Trim only to decide whether an identity was recorded; the value itself is
  // rendered as received.
  const hasIdentity = Boolean(identity?.trim());

  return (
    <div {...(testId ? { "data-testid": `last-changed-${testId}` } : {})}>
      <SpaceBetween size="xxs">
        {hasIdentity ? (
          <Box>{identity}</Box>
        ) : (
          <Box color="text-status-inactive">{NO_IDENTITY_PLACEHOLDER}</Box>
        )}
        {timestamp ? (
          <Box color="text-status-inactive" fontSize="body-s">
            {formatTimestamp(timestamp)}
          </Box>
        ) : null}
      </SpaceBetween>
    </div>
  );
};

export default LastChangedCell;
